import Button from '../../components/form-control/Button';
import { Formik } from 'formik';
import Input from '../../components/form-control/Input';
import * as Yup from 'yup';
import { useState } from 'react';
import { assignMember } from '../../api/requests';
import { Company } from '../../models/company/Company';
import SelectDropdown from '../../components/form-control/SelectDropdown';
import { User } from '../../models/User';
import usePartner from '../../hooks/partnerHook';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { getSelectedCountry } from '../../states/slices/resourceSlice';
import Loader from '../../components/Loader';
import { showToast } from '../../utils/miscFunctions';

export interface IAssignMemberDTO {
  staffEmail: string;
  id: string;
}
export interface AssignMemberProps {
  onProceed: () => void;
  onClose: () => void;
  teamID: string;
  fromUser?: boolean;
  user?: User
}

const AssignMember = ({ onProceed, onClose, teamID, fromUser, user }: AssignMemberProps) => {
  const [submitting, setSubmitting] = useState<boolean>(false);
  const { fetchAllCompanies } = usePartner()
  const country = useSelector(getSelectedCountry)
  const countryId = country?.id
  const queryClient = useQueryClient();

  const submitHandler = (values: any) => {
    setSubmitting(true);
    const payload: IAssignMemberDTO = {
      staffEmail: fromUser ? user?.email : values.email.trim(),
      id: fromUser ? values.companyId : teamID,
    };

    assignMember(payload)
      .then((response: any) => {
        showToast.success(response.message ?? 'Member assigned to company successfully');
        onProceed();
        queryClient.invalidateQueries({ queryKey: ['getTeams'] });
      })
      .catch((error: any) => {
        showToast.error(error?.description);
      })
      .finally(() => setSubmitting(false));
  };

  const { data: companies, isLoading } = useQuery({
    queryKey: ['fetchAllCompanies', countryId],
    queryFn: () => countryId ? fetchAllCompanies(countryId, 0, 100) : null,
    enabled: !!countryId,
  })

  return (
    <div className="py-6 px-3 md:px-6 w-full">
      {isLoading ? (
        <Loader message="Fetching companies please wait..." />
      ) : (
        <Formik
          initialValues={{
            email: '',
            companyId: ''
          }}
          validateOnChange={false}
          validateOnBlur={false}
          validationSchema={
            fromUser
              ? Yup.object({ companyId: Yup.string().required('Company is required.') })
              : Yup.object({ email: Yup.string().email('Email provided invalid.').required('Email is requied.') })
          }
          onSubmit={(values) => {
            submitHandler(values);
          }}>
          {({ values, errors, handleSubmit, handleChange, setFieldValue, handleBlur, touched }) => (
            <form className="space-y-4" onSubmit={handleSubmit}>
              <div className="space-y-5">
                <h3 className="text-lg font-medium text-gray-900 mb-4 dark:text-darkBg-mainText">Assign Member To Company</h3>

                {fromUser ? (
                  <SelectDropdown
                    name="company"
                    label="Select a company to assign this user to"
                    listSelectedValue={values.companyId}
                    list={companies?.map((company: Company) => ({
                      name: company?.name,
                      value: company?.id
                    }))}
                    onChange={(value: string) => { setFieldValue('companyId', value); }}
                    onBlur={handleBlur}
                    error={errors.companyId}
                    helperText={errors.companyId}
                  />
                ) : (
                  <Input
                    name="email"
                    label="Enter email of the user to assign to this company"
                    value={values.email.trim()}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    type="email"
                    required
                    error={touched.email ? errors.email : undefined}
                    helperText={touched?.email ? errors.email : undefined}
                  />
                )
                }
              </div>

              <div className="w-full flex gap-4 mt-6 !mb-4">
                <Button
                  type="button"
                  label="Cancel"
                  onClick={onClose}
                  bgColor='secondary'
                  fullWidth
                  loading={submitting}
                  disabled={submitting}
                />
                <Button
                  type="submit"
                  label="Continue"
                  bgColor='primary'
                  fullWidth
                  loading={submitting}
                  disabled={submitting}
                />
              </div>
            </form>
          )}
        </Formik>
      )}
    </div>
  );
};

export default AssignMember;
